import type { SfxName } from './settings';

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Call from a user gesture so later sounds are allowed to play. */
export function unlockAudio(): void {
  audio();
}

function tone(c: AudioContext, freq: number, start: number, dur: number, gain: number, type: OscillatorType = 'sine', slideTo?: number) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, start);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  o.connect(g).connect(c.destination);
  o.start(start);
  o.stop(start + dur + 0.02);
}

function noise(c: AudioContext, start: number, dur: number, gain: number, from: number, to: number) {
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = 'bandpass';
  f.Q.value = 1.2;
  f.frequency.setValueAtTime(from, start);
  f.frequency.exponentialRampToValueAtTime(to, start + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + dur * 0.3);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  src.connect(f).connect(g).connect(c.destination);
  src.start(start);
}

/** Plays a short synthesized sound. `volume` is 0..1. */
export function playSfx(name: SfxName, volume = 0.8): void {
  if (name === 'off') return;
  const c = audio();
  if (!c) return;
  const v = Math.max(0, Math.min(1, volume)) * 0.5;
  const t = c.currentTime + 0.01;
  switch (name) {
    case 'coin':
      tone(c, 987.77, t, 0.09, v, 'square');
      tone(c, 1318.51, t + 0.09, 0.28, v, 'square');
      break;
    case 'bell':
      tone(c, 880, t, 0.9, v, 'sine');
      tone(c, 1760, t, 0.5, v * 0.4, 'sine');
      tone(c, 2637, t, 0.3, v * 0.2, 'sine');
      break;
    case 'pop':
      tone(c, 520, t, 0.12, v, 'sine', 180);
      break;
    case 'whoosh':
      noise(c, t, 0.45, v * 0.9, 300, 2400);
      break;
    case 'tick':
      tone(c, 1500, t, 0.03, v * 0.7, 'square', 900);
      break;
  }
}
